import React from 'react';
import { Check, Flame, LoaderCircle, X } from 'lucide-react';
import './StreakSummary.css';

function StreakCount({ count, loading, showIcon = true }) {
  const dayLabel = count === 1 ? 'day' : 'days';

  return (
    <div className="streak-count" aria-live="polite">
      {showIcon ? (
        <span className="streak-count__icon" aria-hidden="true">
          <Flame size={22} fill="currentColor" />
        </span>
      ) : null}
      {loading ? (
        <LoaderCircle className="streak-count__loader" size={20} aria-label="Loading streak" />
      ) : (
        <strong>{count} {dayLabel}</strong>
      )}
    </div>
  );
}

function StreakWeekTracker({ days }) {
  if (!days.length) return null;

  return (
    <div className="streak-week" aria-label="Seven day streak activity">
      {days.map((day) => {
        const completed = day.status === 'completed';
        const missed = day.status === 'missed';
        const today = day.status === 'today_pending';

        return (
          <div className="streak-week__day" key={day.date}>
            <span
              className={[
                'streak-week__label',
                completed ? 'streak-week__label--completed' : '',
                today ? 'streak-week__label--today' : '',
              ].filter(Boolean).join(' ')}
            >
              {day.dayLabel}
            </span>
            <span
              className={[
                'streak-week__bubble',
                completed ? 'streak-week__bubble--completed' : '',
                missed ? 'streak-week__bubble--missed' : '',
                today ? 'streak-week__bubble--today' : '',
              ].filter(Boolean).join(' ')}
              title={`${day.dayLabel}: ${day.status.replace('_', ' ')}`}
            >
              {completed ? <Check size={20} strokeWidth={3} /> : null}
              {missed ? <X size={18} strokeWidth={2.5} /> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function StreakSummary({ streak, loading = false, celebration = false, profile = false }) {
  const currentCount = streak?.currentCount ?? 0;
  const days = streak?.weekTracker || [];
  const dayLabel = currentCount === 1 ? 'day' : 'days';

  if (profile) {
    return (
      <section className="streak-summary streak-summary--profile">
        <div className="streak-summary__profile-header">
          <div className="streak-summary__profile-title">
            <span className="streak-count__icon" aria-hidden="true">
              <Flame size={22} fill="currentColor" />
            </span>
            <h2>Practice Streak</h2>
          </div>
          <StreakCount count={currentCount} loading={loading} showIcon={false} />
        </div>
        {!loading ? <StreakWeekTracker days={days} /> : null}
        {!loading && currentCount === 0 ? (
          <p className="streak-summary__profile-prompt">Start a practice session to build your streak.</p>
        ) : null}
      </section>
    );
  }

  return (
    <section className={`streak-summary${celebration ? ' streak-summary--celebration' : ''}`}>
      {celebration && !loading ? (
        <div className="streak-summary__reward">
          <span className="streak-summary__reward-icon" aria-hidden="true">
            <Flame size={46} fill="currentColor" />
          </span>
          <h3>{currentCount} {dayLabel} strong</h3>
          <p>Keep the rhythm going with your next practice.</p>
        </div>
      ) : null}

      <div className="streak-summary__body">
        <StreakCount count={currentCount} loading={loading} />
        {!loading ? <StreakWeekTracker days={days} /> : null}
      </div>
    </section>
  );
}
